import React, { useState, useEffect } from 'react';
import { Building2, Search, MapPin, UserCheck, Calendar, Users } from 'lucide-react';
import { TableRoot, Table, THead, TR, TH, TBody, TD } from '../../components/UI/Table';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { Office } from '../../lib/sdk/index';
import { useOffices, useDonors, useGuestDonors } from '../../hooks/useQueries';

const useDebounce = <T,>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedValue(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
};

const Offices: React.FC = () => {
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 350);
  const limit = 50;
  
  // Use React Query hooks with pagination
  const { data: officesResponse, isLoading: loading } = useOffices({ 
    page, 
    limit, 
    search: debouncedSearchTerm || undefined 
  });
  
  // Get all donors and guest donors for counting (without pagination for counts)
  const { data: donorsResponse } = useDonors();
  const { data: guestDonorsResponse } = useGuestDonors();
  
  const offices = Array.isArray(officesResponse?.data) ? officesResponse.data : [];
  const pagination = officesResponse?.pagination;
  const donors = Array.isArray(donorsResponse?.data) ? donorsResponse.data : [];
  const guestDonors = Array.isArray(guestDonorsResponse?.data) ? guestDonorsResponse.data : [];
  
  // Calculate donor counts per office
  const donorCounts: {[key: string]: number} = {};
  [...donors, ...guestDonors].forEach((donor: any) => {
    const officeId = typeof donor.officeId === 'string' ? donor.officeId : donor.officeId?._id;
    if (officeId) {
      donorCounts[officeId] = (donorCounts[officeId] || 0) + 1;
    }
  });
  
  // Reset to page 1 when search term changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Offices</h1>
        <p className="text-gray-600 dark:text-gray-400">View and search offices</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <Building2 className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Offices</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{offices.length}</p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <MapPin className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Central Office</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {offices.filter(office => office.isCentral).length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
              <MapPin className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Regional Offices</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {offices.filter(office => !office.isCentral).length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
              <UserCheck className="h-6 w-6 text-orange-600 dark:text-orange-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Assigned Controllers</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {new Set(offices.filter(office => office.controllerId).map(office => 
                  typeof office.controllerId === 'string' ? office.controllerId : office.controllerId._id
                )).size}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder="Search offices by name, number, controller, or region..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="form-input pl-10 w-full"
          />
        </div>
      </div>

      <div className="card">
        {offices.length === 0 ? (
          <div className="text-center py-12">
            <Building2 className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No offices found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Get started by adding your first office.
            </p>
          </div>
        ) : (
          <>
            <TableRoot>
              <Table>
                <THead>
                  <TR>
                    <TH>Office ID</TH>
                    <TH>Name</TH>
                    <TH>Controller</TH>
                    <TH>Region</TH>
                    <TH>No. Of Donors</TH>
                  </TR>
                </THead>
                <TBody>
                  {offices.map((office) => (
                  <TR key={office._id}>
                    <TD className="font-medium">{office.officeNo}</TD>
                    <TD>{office.name}</TD>
                    <TD>
                      {office.controllerId && typeof office.controllerId === 'object' && 'name' in office.controllerId 
                        ? (office.controllerId as any).name 
                        : 'Unassigned'
                      }
                    </TD>
                    <TD>
                      {office.isCentral ? 'Central Office' : (
                        office.regionId && typeof office.regionId === 'object' && 'name' in office.regionId 
                          ? (office.regionId as any).name 
                          : 'Unassigned'
                      )}
                    </TD>
                    <TD className="text-center">
                      <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                        {donorCounts[office._id] || 0}
                      </span>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </TableRoot>
          
          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 px-4 py-3 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} offices
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={pagination.page === 1 || loading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
                >
                  Previous
                </button>
                <span className="flex items-center px-4 text-sm text-gray-700 dark:text-gray-300">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                  disabled={pagination.page === pagination.totalPages || loading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
                >
                  Next
                </button>
              </div>
            </div>
          )}
          </>
        )}
      </div>
    </div>
  );
};

export default Offices;
